import { Container, ContainerProps, Text, Box } from "@chakra-ui/react";
import Link from "next/link";
import { IArtistCard } from "@interfaces";
import { FadeInUpBox, IntersectionObserver, StaggerWrap } from "@motion";
import Masonry from "react-masonry-css";

interface IArtistsCollections {
  collection: IArtistCard[];
  containerProps?: ContainerProps;
}

const ArtistsCollections = ({
                              collection,
                              containerProps,
                            }: IArtistsCollections) => {
  // Türkçe karakterleri İngilizce karşılıklarına dönüştürür
  const getFirstLetter = (name: string): string => {
    if (!name) return "";
    const firstChar = name.trim().charAt(0).toUpperCase();
    const turkishMap: Record<string, string> = {
      Ç: "C",
      Ğ: "G",
      İ: "I",
      Ö: "O",
      Ş: "S",
      Ü: "U",
    };
    return turkishMap[firstChar] || firstChar;
  };

  // Sanatçıları baş harfe göre grupla
  const groupedArtists = collection.reduce(
    (acc: Record<string, IArtistCard[]>, artist) => {
      const firstLetter = getFirstLetter(artist.fullName);
      if (!acc[firstLetter]) acc[firstLetter] = [];
      acc[firstLetter].push(artist);
      return acc;
    },
    {}
  );

  const sortedGroups = Object.keys(groupedArtists).sort();

  return (
    <>
      <style jsx global>{`
        .masonry-grid {
          display: flex;
          margin-left: -10px;
          width: auto;
        }
        .masonry-grid_column {
          padding-left: 20px;
          background-clip: padding-box;
        }
        .masonry-grid_column > div {
          margin-bottom: 10px;
        }
      `}</style>

      <Container
        maxWidth="container.xl"
        px={{ base: "padding.primary", lg: "0" }}
        pt={{ base: "10", lg: "14" }}
        pb={{ base: "padding.primary2", lg: "padding.primary4" }}
        {...containerProps}
      >
        <StaggerWrap staggerChildren={1.6}>
          <Masonry
            breakpointCols={{ default: 6, 1170: 2, 768: 1 }}
            className="masonry-grid"
            columnClassName="masonry-grid_column"
            style={{
              background: "#faf9f9",
              padding: "10px",
              borderTop: "2px solid #c71f3f",
            }}
          >
            {sortedGroups.map((letter) => (
              <IntersectionObserver key={letter}>
                <FadeInUpBox duration={1}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    mb={3}
                    p={4}
                    justifyContent="flex-end"
                  >
                    <Text
                      fontSize="xl"
                      fontWeight="bold"
                      color="red.primary"
                      mb={2}
                      borderBottom="2px solid"
                      borderColor="white.primary3"
                      pb={1}
                    >
                      {letter}
                    </Text>

                    <Box display="flex" flexDirection="column" gap={1}>
                      {groupedArtists[letter].map((artist) => {
                        const hasValidUrl =
                          typeof artist.url === "string" &&
                          artist.url.trim().length > 0;

                        return hasValidUrl ? (
                          <Link
                            key={artist.sourceId}
                            href={artist.url}
                            style={{ textDecoration: "none" }}
                          >
                            <Box
                              _hover={{
                                bg: "gray.50",
                                cursor: "pointer",
                              }}
                              p={1}
                              borderRadius="md"
                              display="block"
                              transition="background 0.2s"
                            >
                              <Text fontSize="sm" fontWeight="medium">
                                {artist.fullName}
                              </Text>
                              {artist.lifeRange && (
                                <Text fontSize="xs" color="gray.600">
                                  {artist.lifeRange}
                                </Text>
                              )}
                            </Box>
                          </Link>
                        ) : (
                          <Box
                            key={artist.sourceId}
                            p={1}
                            borderRadius="md"
                            display="block"
                            transition="background 0.2s"
                            _hover={{ bg: "gray.50" }}
                          >
                            <Text fontSize="sm" fontWeight="medium">
                              {artist.fullName}
                            </Text>
                            {artist.lifeRange && (
                              <Text fontSize="xs" color="gray.600">
                                {artist.lifeRange}
                              </Text>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </FadeInUpBox>
              </IntersectionObserver>
            ))}
          </Masonry>
        </StaggerWrap>
      </Container>
    </>
  );
};

export default ArtistsCollections;
